import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { contactApi } from '../services';
import { useAuth } from './AuthGate';
import Icon from './Icon';
import { managerInitials } from './RelationshipManagersField';
import './RelationshipManagers.css';

const EMPTY_FORM = { name: '', title: '', email: '', phone: '', aliases: '', notes: '' };

const toForm = (manager) => (manager ? {
  name: manager.name || '',
  title: manager.title || '',
  email: manager.email || '',
  phone: manager.phone || '',
  aliases: (manager.aliases || []).join(', '),
  notes: manager.notes || ''
} : EMPTY_FORM);

// Empty strings clear a field
const fromForm = (form) => ({
  name: form.name.trim(),
  title: form.title.trim(),
  email: form.email.trim(),
  phone: form.phone.trim(),
  aliases: form.aliases.split(',').map(alias => alias.trim()).filter(Boolean),
  notes: form.notes.trim()
});

const companiesLabel = (count) => (count ? `${count} ${count === 1 ? 'company' : 'companies'}` : 'No companies');

const ManagerForm = ({ manager, onSaved, onCancel }) => {
  const [form, setForm] = useState(() => toForm(manager));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const nameRef = useRef(null);
  const id = (field) => `rm-form-${manager ? manager.id : 'new'}-${field}`;

  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  const update = (field) => (event) => setForm(current => ({ ...current, [field]: event.target.value }));

  const submit = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) {
      setError('A name is required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const fields = fromForm(form);
      onSaved(manager
        ? await contactApi.updateRelationshipManager(manager.id, fields)
        : await contactApi.createRelationshipManager(fields));
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <form
      className="rm-form"
      onSubmit={submit}
      noValidate
      aria-label={manager ? `Edit ${manager.name}` : 'New relationship manager'}
    >
      <div className="rm-form__grid">
        <div className="rm-form__field">
          <label htmlFor={id('name')}>Name</label>
          <input id={id('name')} ref={nameRef} className="rm-input" value={form.name} onChange={update('name')}
                 maxLength={120} required aria-required="true" autoComplete="off" />
        </div>
        <div className="rm-form__field">
          <label htmlFor={id('title')}>Title</label>
          <input id={id('title')} className="rm-input" value={form.title} onChange={update('title')}
                 maxLength={120} placeholder="e.g. Partner" autoComplete="off" />
        </div>
        <div className="rm-form__field">
          <label htmlFor={id('email')}>Email</label>
          <input id={id('email')} type="email" className="rm-input" value={form.email} onChange={update('email')}
                 maxLength={320} autoComplete="off" />
        </div>
        <div className="rm-form__field">
          <label htmlFor={id('phone')}>Phone</label>
          <input id={id('phone')} type="tel" className="rm-input" value={form.phone} onChange={update('phone')}
                 maxLength={64} autoComplete="off" />
        </div>
        <div className="rm-form__field rm-form__field--wide">
          <label htmlFor={id('aliases')}>Other names</label>
          <input id={id('aliases')} className="rm-input" value={form.aliases} onChange={update('aliases')}
                 aria-describedby={id('aliases-hint')} autoComplete="off" />
          <span id={id('aliases-hint')} className="rm-form__hint">
            Comma-separated. How imports write them, e.g. in the workbook's Owner column.
          </span>
        </div>
        <div className="rm-form__field rm-form__field--wide">
          <label htmlFor={id('notes')}>Notes</label>
          <textarea id={id('notes')} className="rm-input" rows={2} value={form.notes} onChange={update('notes')}
                    maxLength={2000} />
        </div>
      </div>
      {error && <p className="rm-error" role="alert"><Icon name="alert-triangle" /> {error}</p>}
      <div className="rm-buttons">
        <button type="submit" className="rm-button rm-button--primary" disabled={saving}>
          {saving ? 'Saving...' : manager ? 'Save' : 'Add manager'}
        </button>
        <button type="button" className="rm-button" onClick={onCancel} disabled={saving}>Cancel</button>
      </div>
    </form>
  );
};

const ManagerRow = ({ manager, canEdit, busy, error, onEdit, onToggleActive, onDelete }) => {
  const [showCompanies, setShowCompanies] = useState(false);
  const [companies, setCompanies] = useState(null);
  const [companiesError, setCompaniesError] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  useEffect(() => {
    if (!showCompanies) return undefined;
    let cancelled = false;
    setCompaniesError(null);
    contactApi.getRelationshipManager(manager.id)
      .then(detail => {
        if (!cancelled) setCompanies(detail.companies || []);
      })
      .catch(err => {
        if (!cancelled) setCompaniesError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [showCompanies, manager.id, manager.company_count]);

  const listId = `rm-companies-${manager.id}`;

  return (
    <li className={`rm-row${manager.active ? '' : ' rm-row--inactive'}`}>
      <span className="rm-avatar" aria-hidden="true">{managerInitials(manager.name)}</span>
      <div className="rm-row__main">
        <div className="rm-row__name">
          {manager.name}
          {!manager.active && <span className="rm-badge">Inactive</span>}
        </div>
        {manager.title && <div className="rm-row__meta">{manager.title}</div>}
        {(manager.email || manager.phone) && (
          <div className="rm-row__contact">
            {manager.email && (
              <a href={`mailto:${manager.email}`}><Icon name="mail" /> {manager.email}</a>
            )}
            {manager.phone && <span><Icon name="phone" /> {manager.phone}</span>}
          </div>
        )}
        {manager.aliases?.length > 0 && (
          <div className="rm-row__meta">Also written as {manager.aliases.join(', ')}</div>
        )}
        {manager.notes && <p className="rm-row__notes">{manager.notes}</p>}
        {error && <p className="rm-error" role="alert"><Icon name="alert-triangle" /> {error}</p>}
      </div>

      <div className="rm-row__side">
        {manager.company_count > 0 ? (
          <button
            type="button"
            className="rm-link"
            aria-expanded={showCompanies}
            aria-controls={listId}
            onClick={() => setShowCompanies(open => !open)}
          >
            {companiesLabel(manager.company_count)}
            <Icon name={showCompanies ? 'chevron-down' : 'chevron-right'} />
          </button>
        ) : (
          <span className="rm-row__meta">{companiesLabel(0)}</span>
        )}
        {!canEdit ? null : confirmingDelete ? (
          <div className="rm-row__actions" role="group" aria-label={`Delete ${manager.name}?`}>
            <span className="rm-row__meta">Delete {manager.name}?</span>
            <button type="button" className="rm-button rm-button--danger" disabled={busy}
                    onClick={() => onDelete(manager)}>
              Delete
            </button>
            <button type="button" className="rm-button" disabled={busy} onClick={() => setConfirmingDelete(false)}>
              Keep
            </button>
          </div>
        ) : (
          <div className="rm-row__actions">
            <button type="button" className="rm-icon-button" onClick={onEdit} disabled={busy}
                    aria-label={`Edit ${manager.name}`} title="Edit">
              <Icon name="pencil" />
            </button>
            <button type="button" className="rm-button rm-button--quiet" disabled={busy}
                    onClick={() => onToggleActive(manager)}>
              {manager.active ? 'Mark inactive' : 'Make active'}
            </button>
            {manager.company_count === 0 && (
              <button type="button" className="rm-icon-button rm-icon-button--danger" disabled={busy}
                      onClick={() => setConfirmingDelete(true)} aria-label={`Delete ${manager.name}`} title="Delete">
                <Icon name="trash" />
              </button>
            )}
          </div>
        )}
      </div>

      {showCompanies && (
        <div className="rm-row__companies" id={listId}>
          {companiesError && <p className="rm-error" role="alert"><Icon name="alert-triangle" /> {companiesError}</p>}
          {!companies && !companiesError && (
            <p className="placeholder-text" role="status"><Icon name="loader" spin /> Loading...</p>
          )}
          {companies && (
            <ul>
              {companies.map(company => (
                <li key={company.id}>
                  {company.name}
                  {company.on_target_list && (
                    <span className="rm-row__target" title="On an ICP target list"><Icon name="target" /></span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </li>
  );
};

/**
 * The relationship-manager master list: who on our side manages relationships with
 * companies. Add, edit, deactivate, or delete people who manage nothing.
 */
const RelationshipManagersPanel = ({ isOpen, onClose, onDataChanged }) => {
  const { isAdmin } = useAuth();
  const [managers, setManagers] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  // A manager id, 'new' for the add form, or null
  const [editingId, setEditingId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [rowError, setRowError] = useState(null);
  const dialogRef = useRef(null);

  const load = useCallback(async () => {
    try {
      setManagers(await contactApi.getRelationshipManagers(true));
      setLoadError(null);
    } catch (err) {
      setLoadError(err.message);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setManagers(null);
    setEditingId(null);
    setRowError(null);
    load();
    dialogRef.current?.focus();
  }, [isOpen, load]);

  // Escape closes the open form first, then the dialog; it doesn't reach the panels underneath
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      if (editingId !== null) setEditingId(null);
      else onClose();
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen, editingId, onClose]);

  const changed = useCallback(async () => {
    await load();
    onDataChanged?.();
  }, [load, onDataChanged]);

  const handleSaved = () => {
    setEditingId(null);
    changed();
  };

  const runRowAction = async (manager, action) => {
    setBusyId(manager.id);
    setRowError(null);
    try {
      await action();
      await changed();
    } catch (err) {
      setRowError({ id: manager.id, message: err.message });
    } finally {
      setBusyId(null);
    }
  };

  if (!isOpen) return null;

  const all = managers || [];
  const inactiveCount = all.filter(manager => !manager.active).length;
  const visible = all.filter(manager => showInactive || manager.active);

  return createPortal(
    <>
      <div className="rm-overlay" onClick={onClose} aria-hidden="true" />
      <div className="rm-dialog" role="dialog" aria-modal="true" aria-labelledby="rm-dialog-title"
           ref={dialogRef} tabIndex={-1}>
        <header className="rm-dialog__header">
          <div>
            <h2 id="rm-dialog-title"><Icon name="user-check" /> Relationship managers</h2>
            <p>
              Who on our side manages the relationship with each company. Pick a company's managers in its panel.
              {!isAdmin && ' Only admins can change this list.'}
            </p>
          </div>
          <button type="button" className="rm-icon-button" onClick={onClose} aria-label="Close">
            <Icon name="x" />
          </button>
        </header>

        <div className="rm-dialog__toolbar">
          {isAdmin && (
            <button type="button" className="rm-button rm-button--primary" onClick={() => setEditingId('new')}
                    disabled={editingId === 'new'}>
              <Icon name="plus" /> Add manager
            </button>
          )}
          {inactiveCount > 0 && (
            <label className="rm-toggle">
              <input type="checkbox" checked={showInactive} onChange={(event) => setShowInactive(event.target.checked)} />
              Show inactive ({inactiveCount})
            </label>
          )}
        </div>

        <div className="rm-dialog__body">
          {editingId === 'new' && (
            <div className="rm-row rm-row--editing">
              <ManagerForm onSaved={handleSaved} onCancel={() => setEditingId(null)} />
            </div>
          )}
          {loadError && <p className="rm-error" role="alert"><Icon name="alert-triangle" /> {loadError}</p>}
          {!managers && !loadError && (
            <p className="placeholder-text" role="status"><Icon name="loader" spin /> Loading...</p>
          )}
          {managers && visible.length === 0 && editingId !== 'new' && (
            <p className="rm-empty">Nobody is on the list yet. Add someone, or import the ICP workbook: its Owner column fills the list in.</p>
          )}
          <ul className="rm-list">
            {visible.map(manager => (editingId === manager.id ? (
              <li key={manager.id} className="rm-row rm-row--editing">
                <ManagerForm manager={manager} onSaved={handleSaved} onCancel={() => setEditingId(null)} />
              </li>
            ) : (
              <ManagerRow
                key={manager.id}
                manager={manager}
                canEdit={isAdmin}
                busy={busyId === manager.id}
                error={rowError?.id === manager.id ? rowError.message : null}
                onEdit={() => setEditingId(manager.id)}
                onToggleActive={(row) => runRowAction(row, () => contactApi.updateRelationshipManager(row.id, { active: !row.active }))}
                onDelete={(row) => runRowAction(row, () => contactApi.deleteRelationshipManager(row.id))}
              />
            )))}
          </ul>
        </div>
      </div>
    </>,
    document.body
  );
};

export default RelationshipManagersPanel;
